import { useMemo, useState } from "react";
import axios from "axios";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { useSaveStatus } from "./useSaveStatus";
import OrgOwnerCard from "./OrgOwnerCard";
import OrgAccessLimitsCard from "./OrgAccessLimitsCard";
import OrgUsersList from "./OrgUsersList";
import {
  InviteUserModal, EditUserModal, ChangeRoleModal, ManageAccessModal,
  TransferOwnershipModal, ViewProfileModal, ConfirmActionModal,
} from "./OrgUserModals";

export default function OrgUsersTab({ org, ownerInvitationStatus, limits, usage, users, memberships, invitations, API, onSaved, reload }) {
  const [owner, setOwner] = useState(() => ({
    full_name: org.owner?.full_name || "",
    email: org.owner?.email || "",
    phone: org.owner?.phone || "",
    job_title: org.owner?.job_title || "",
  }));
  const [limitsForm, setLimitsForm] = useState(() => ({
    max_org_admins: limits?.max_org_admins ?? 3,
    max_teams: limits?.max_teams ?? 5,
    max_users_per_team: limits?.max_users_per_team ?? 10,
    max_nova_billing_users: limits?.max_nova_billing_users ?? 2,
  }));
  const ownerStatus = useSaveStatus();
  const limitsStatus = useSaveStatus();
  const ownerEstablished = ownerInvitationStatus === "Accepted";

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [modal, setModal] = useState(null);
  const [modalSaving, setModalSaving] = useState(false);
  const [modalError, setModalError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [actionMessage, setActionMessage] = useState("");
  const [actionError, setActionError] = useState("");

  const rows = useMemo(() => {
    const membershipByUser = new Map((memberships || []).map((m) => [m.user_id, m]));
    const userRows = (users || []).map((u) => {
      const membership = membershipByUser.get(u.user_id) || {};
      const role = u.role || membership.role;
      const isOwner = role === "org_owner";
      return {
        id: `user_${u.user_id}`, kind: "user", userId: u.user_id, name: u.name, email: u.email, role, isOwner,
        novaBilling: isOwner || !!membership.nova_billing_access,
        catalogManage: isOwner || !!membership.catalog_manage,
        status: u.disabled_at ? "Deactivated" : "Active",
        lastActive: u.last_login_at,
        passwordEligible: !!u.password_eligible,
      };
    });
    const now = Date.now();
    const inviteRows = (invitations || [])
      .filter((inv) => !inv.used_at && !inv.is_owner)
      .map((inv) => ({
        id: `invite_${inv.invite_id}`, kind: "invite", inviteId: inv.invite_id, name: inv.name, email: inv.email,
        role: inv.role, isOwner: false, novaBilling: false,
        status: new Date(inv.expires_at).getTime() > now ? "Invited" : "Expired",
        lastActive: null, passwordEligible: false,
      }));
    return [...userRows, ...inviteRows];
  }, [users, memberships, invitations]);

  const counts = useMemo(() => ({
    all: rows.length,
    owners: rows.filter((r) => r.role === "org_owner").length,
    org_admins: rows.filter((r) => r.role === "org_admin").length,
    billing: rows.filter((r) => r.kind === "user" && r.novaBilling).length,
  }), [rows]);

  const filteredRows = useMemo(() => {
    let list = rows;
    if (filter === "owners") list = list.filter((r) => r.role === "org_owner");
    else if (filter === "org_admins") list = list.filter((r) => r.role === "org_admin");
    else if (filter === "billing") list = list.filter((r) => r.kind === "user" && r.novaBilling);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((r) => (r.name || "").toLowerCase().includes(q) || (r.email || "").toLowerCase().includes(q));
    return list;
  }, [rows, filter, search]);

  const transferCandidates = useMemo(
    () => rows.filter((r) => r.kind === "user" && !r.isOwner && r.status === "Active"),
    [rows],
  );

  const sendOwnerInvite = async () => {
    ownerStatus.setSaving(true);
    ownerStatus.setError("");
    try {
      const { data } = await axios.post(`${API}/admin/organizations/${org.org_id}/owner-invitation`, owner, { withCredentials: true });
      onSaved(data);
      ownerStatus.flash();
    } catch (err) {
      ownerStatus.setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      ownerStatus.setSaving(false);
    }
  };

  const saveLimits = async () => {
    limitsStatus.setSaving(true);
    limitsStatus.setError("");
    try {
      const { data } = await axios.patch(`${API}/admin/organizations/${org.org_id}/limits`, limitsForm, { withCredentials: true });
      onSaved(data);
      limitsStatus.flash();
    } catch (err) {
      limitsStatus.setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      limitsStatus.setSaving(false);
    }
  };

  const closeModal = () => { setModal(null); setModalError(""); setModalSaving(false); };

  const runDirect = async (id, fn, successMessage) => {
    setActionError(""); setActionMessage("");
    setBusyId(id);
    try {
      await fn();
      setActionMessage(successMessage);
      await reload();
    } catch (err) {
      setActionError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const onRowAction = (type, row) => {
    setActionError(""); setActionMessage("");
    if (type === "resend_invite") {
      return runDirect(row.id, () => axios.post(`${API}/admin/organizations/${org.org_id}/invitations/${row.inviteId}/resend`, {}, { withCredentials: true }), "Invitation resent.");
    }
    if (type === "deactivate") {
      return runDirect(row.id, () => axios.patch(`${API}/admin/users/${row.userId}/status`, { disabled: true }, { withCredentials: true }), "User deactivated.");
    }
    if (type === "reactivate") {
      return runDirect(row.id, () => axios.patch(`${API}/admin/users/${row.userId}/status`, { disabled: false }, { withCredentials: true }), "User reactivated.");
    }
    if (type === "reset_password") {
      return runDirect(row.id, () => axios.post(`${API}/admin/users/${row.userId}/send-password-reset`, {}, { withCredentials: true }), "Password reset link sent.");
    }
    setModal({ type, row });
  };

  const submitModal = async (fn) => {
    setModalSaving(true);
    setModalError("");
    try {
      await fn();
      await reload();
      closeModal();
    } catch (err) {
      setModalError(formatApiErrorDetail(err.response?.data?.detail));
      setModalSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="org-users-tab">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[380px_1fr]">
        <OrgOwnerCard
          org={org}
          ownerEstablished={ownerEstablished}
          ownerInvitationStatus={ownerInvitationStatus}
          owner={owner}
          setOwner={setOwner}
          onSendInvite={sendOwnerInvite}
          ownerStatus={ownerStatus}
        />
        <OrgAccessLimitsCard limits={limits} limitsForm={limitsForm} setLimitsForm={setLimitsForm} usage={usage} limitsStatus={limitsStatus} onSave={saveLimits} />
      </div>

      {actionMessage && <p className="rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400" data-testid="org-users-action-message">{actionMessage}</p>}
      {actionError && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="org-users-action-error">{actionError}</p>}

      <OrgUsersList
        rows={filteredRows}
        counts={counts}
        filter={filter}
        onFilterChange={setFilter}
        search={search}
        onSearchChange={setSearch}
        onAction={onRowAction}
        onInvite={() => setModal({ type: "invite", row: null })}
        busyId={busyId}
      />

      {modal?.type === "invite" && (
        <InviteUserModal
          onClose={closeModal}
          saving={modalSaving}
          error={modalError}
          onSubmit={(form) => submitModal(() => axios.post(`${API}/admin/organizations/${org.org_id}/invitations`, form, { withCredentials: true }))}
        />
      )}
      {modal?.type === "edit" && (
        <EditUserModal
          row={modal.row}
          onClose={closeModal}
          saving={modalSaving}
          error={modalError}
          onSubmit={(form) => submitModal(() => axios.patch(`${API}/admin/users/${modal.row.userId}`, form, { withCredentials: true }))}
        />
      )}
      {modal?.type === "role" && (
        <ChangeRoleModal
          row={modal.row}
          onClose={closeModal}
          saving={modalSaving}
          error={modalError}
          onSubmit={(form) => submitModal(() => axios.patch(`${API}/admin/users/${modal.row.userId}`, form, { withCredentials: true }))}
        />
      )}
      {modal?.type === "access" && (
        <ManageAccessModal
          row={modal.row}
          onClose={closeModal}
          saving={modalSaving}
          error={modalError}
          onSubmit={(form) => submitModal(() => axios.patch(`${API}/admin/organizations/${org.org_id}/users/${modal.row.userId}/access`, form, { withCredentials: true }))}
        />
      )}
      {modal?.type === "transfer" && (
        <TransferOwnershipModal
          row={modal.row}
          candidates={transferCandidates}
          onClose={closeModal}
          saving={modalSaving}
          error={modalError}
          onSubmit={({ newOwnerId }) => submitModal(() => axios.patch(`${API}/admin/users/${newOwnerId}`, { role: "org_owner" }, { withCredentials: true }))}
        />
      )}
      {modal?.type === "view" && <ViewProfileModal row={modal.row} onClose={closeModal} />}
      {modal?.type === "remove" && (
        <ConfirmActionModal
          title="Remove this user?"
          message={`${modal.row.name || modal.row.email} will be permanently removed. This can be restored later from the platform Users page if needed.`}
          confirmLabel="Yes, remove"
          danger
          onClose={closeModal}
          saving={modalSaving}
          error={modalError}
          onConfirm={() => submitModal(() => axios.delete(`${API}/admin/users/${modal.row.userId}`, { withCredentials: true }))}
        />
      )}
      {modal?.type === "cancel_invite" && (
        <ConfirmActionModal
          title="Cancel this invitation?"
          message={`The pending invitation to ${modal.row.email} will be cancelled.`}
          confirmLabel="Yes, cancel"
          danger
          onClose={closeModal}
          saving={modalSaving}
          error={modalError}
          onConfirm={() => submitModal(() => axios.delete(`${API}/admin/organizations/${org.org_id}/invitations/${modal.row.inviteId}`, { withCredentials: true }))}
        />
      )}
    </div>
  );
}
